import { useMemo } from "react"
import { get, isEmpty, compact, isFunction, chain, isNil } from "lodash"
import { usePositionSetMetadata } from "resources/position-sets"
import { INSTRUMENTS, INSTRUMENTS_BY_MODE } from "connected-components/dashboard/helpers"
import { DELTA_SYMBOL } from "helpers/assets/constants"
import { VALUE_FORMATS } from "helpers/metrics/constants"
import { getExcelValue } from "helpers/csv"
import { buildContributorKey } from "helpers/contributors"
import { OK } from "helpers/data-statuses"

export function getContributorLabel(contributor) {
  const tickerPart = contributor.assetClass === "equity"
    ? contributor.id.split(".")[0]
    : contributor.id

  if (!tickerPart) {
    return "Unknown"
  }

  const countryPart = contributor.country && contributor.country !== "US"
    ? ` ${contributor.country}`
    : ""

  return tickerPart + countryPart
}

// [TODO] explore the possibility of using Symbol. Does weird things with for..in loops.
export const NOT_HELD = "notheld"

function getBetaInstrumentType(instrument) {
  if (instrument.type) {
    return instrument.type
  }
  if (instrument.securityId) {
    return "ETF"
  }
  if (instrument.resource) {
    return instrument.resource.type
  }
  if (instrument.benchmarkId) {
    return "BENCHMARK"
  }
  if (instrument.indexId) {
    return "INDEX"
  }
  return null
}

export function usePositionSetBetaInstrument(positionSetMetadata = {}, modelMetadata = {}) {
  const psBetaInstrument = useMemo(() => {
    if (!positionSetMetadata.betaInstrument) {
      return null
    }
    const instrument = positionSetMetadata.betaInstrument?.resource || positionSetMetadata.betaInstrument
    const type = getBetaInstrumentType(instrument)
    switch (type) {
      case "INDEX":
        return { ...instrument, id: instrument.indexId, type }
      case "ETF":
        return { ...instrument, ...instrument.securityId, id: instrument.securityId.internalId, type }
      case "BENCHMARK":
        return { ...instrument, id: instrument.benchmarkId, type }
      default:
        return { ...instrument, id: instrument.alias, type }
    }
  }, [positionSetMetadata])

  const date = modelMetadata?.date || modelMetadata?.availability?.currentDate
  const [betaInstrument, betaInstrumentStatus] = usePositionSetMetadata(modelMetadata?.id, psBetaInstrument, date)
  return useMemo(() => {
    if (betaInstrumentStatus !== OK || !betaInstrument) {
      return null
    }

    return {
      ...psBetaInstrument,
      ...betaInstrument,
      description: betaInstrument.description || betaInstrument.name,
    }
  }, [betaInstrumentStatus, betaInstrument, psBetaInstrument])
}

export function getGatherersFromInstruments(availableInstruments, handleGetInstrumentGatherers) {
  return Object.entries(availableInstruments).reduce((acc, [instrumentType, instrument]) => {
    const suffix = INSTRUMENTS[instrumentType].metricsSuffix
    return [...acc, ...handleGetInstrumentGatherers(instrument, suffix, instrumentType)]
  }, [])
}

// This regex is used to strip the suffix from the metric id
// For example, if the metric id is "risk-tertiary.riskDecomposition", the suffix is "-tertiary"
// So the stripped id is "risk.riskDecomposition"
const idSuffixesRegex = chain(INSTRUMENTS)
  .map(({ suffix }) => suffix)
  .compact()
  .join("|")
  .thru((regex) => new RegExp(`${regex}$`))
  .value()

export function getStrippedId(metric) {
  return (metric.id || "").replace(idSuffixesRegex, "")
}

function shouldShowDownloadMetric(metric, existingMetrics) {
  if (!metric.onlyShowOnceInDownload) {
    return true
  }

  return !existingMetrics[getStrippedId(metric)]
}

function makeDownloadHeaders(metrics) {
  // If a particular metric or header should only be shown once in the download, even across multiple instruments,
  // we need to keep track of the root metric/header and skip its subsequent variants.
  const seenMetrics = {}
  return metrics.reduce((downloadHeaders, metric) => {
    const key = metric.onlyShowOnceInDownload ? getStrippedId(metric) : null
    if (!key || !seenMetrics[key]) {
      downloadHeaders.push(metric.downloadLabel || metric.label)
    }
    seenMetrics[key] = true
    return downloadHeaders
  }, [])
}

export function getMetricValue(metric, item) {
  if (isFunction(metric.dataGetter)) {
    return metric.dataGetter(item.values, { dataPath: metric.dataPath, label: item.label })
  } else if (metric.dataPath) {
    return get(item.values, metric.dataPath)
  } else {
    return get(item, metric.id)
  }
}

export function formatMetricValue(value, metric) {
  const format = metric.format || metric.outputFormat
  const isPercent = format === VALUE_FORMATS.PERCENT || format === VALUE_FORMATS.PERCENT_100
  const adjustedValue = format === VALUE_FORMATS.PERCENT_100 ? value / 100 : value
  const formattedValue = isNaN(adjustedValue) ? adjustedValue : getExcelValue(adjustedValue, { percent: isPercent })
  return formattedValue || ""
}

function makeDownloadRows(data, metrics) {
  if (isEmpty(data)) {
    return []
  }

  return data.map((item) => {
    const seenMetrics = {}
    return chain(metrics)
      .map((metric) => {
        if (!shouldShowDownloadMetric(metric, seenMetrics)) {
          return null
        }
        seenMetrics[getStrippedId(metric)] = true
        const value = getMetricValue(metric, item)
        return formatMetricValue(value, metric)
      })
      .filter((value) => !isNil(value))
      .value()
  })
}

export function makeAssetViewDownloadData(values) {
  if (isEmpty(values)) {
    return null
  }
  const { data: metricData, metrics, name } = values
  return {
    name,
    data: {
      headers: makeDownloadHeaders(metrics),
      rows: compact(makeDownloadRows(metricData, metrics)),
    },
  }
}

// marginal and marginalContribution are used interchangeably based on OP templates vs custom metrics
const METRICS_WITHOUT_MAGNITUDE_ORDERING = ["marginal", "marginalContribution", "beta"]

export const COLUMN_ORDER_OPTIONS_ID = {
  CUSTOM: "custom",
  DEFAULT: "default",
}

export function useColumnOrderOptions(selectedView, selectedMode) {
  return useMemo(() => {
    if (METRICS_WITHOUT_MAGNITUDE_ORDERING.includes(selectedView)) {
      return { options: null, order: null }
    }
    const canOrderByMagnitude = selectedView !== "custom"

    const instruments = INSTRUMENTS_BY_MODE[selectedMode]
    let magnitudeId = instruments[0]
    let magnitudeLabel = "Biggest Contributor"
    if (instruments.length > 1) {
      magnitudeId = "delta"
      magnitudeLabel = `Biggest Contributor (${DELTA_SYMBOL})`
    }

    const options = compact([{
      id: COLUMN_ORDER_OPTIONS_ID.DEFAULT,
      primaryText: "Default",
      secondaryText: "The view's default ordering",
    }, canOrderByMagnitude && {
      id: magnitudeId,
      primaryText: magnitudeLabel,
      secondaryText: "Ordered high to low by contribution to this instrument",
    }, {
      id: COLUMN_ORDER_OPTIONS_ID.CUSTOM,
      primaryText: "Custom",
      secondaryText: "Your saved column order from drag and drop",
    }])

    return { options, order: canOrderByMagnitude ? magnitudeId : "default" }
  }, [selectedView, selectedMode])
}

// The rebalancedInstrument here is used to prevent showing trade highlights before the rebalance is applied
// When a trade is added, the query for the rebalancedInstrument are fired and the rebalancedInstrument is set to null
export function useHighlightedTradeRowIds(trades, rebalancedInstrument) {
  return useMemo(() => {
    if (!rebalancedInstrument) {
      return []
    }
    return trades.map(trade => {
      // Trades don't have modelProviderId, but the contributor id depends on it for indexes
      const security = {
        ...trade.security,
        ...(trade.security.assetClass === "index" && { modelProviderId: `i.${trade.security.id}` }),
      }
      return buildContributorKey(security)
    })
  }, [trades, rebalancedInstrument])
}
